<template>
  <div ref="containerElement" class="relative w-full max-w-full pt-[198px]">
    <div
      ref="fixedElement"
      class="fixed bottom-0 bg-surface-primary border-t-md border-border-tertiary"
    >
      <div class="flex flex-col p-16 md:p-24">
        <div aria-live="polite" aria-atomic>
          <span>
            <AppErrorText v-if="familyStore.ui.generalErrorMessage">
              {{ familyStore.ui.generalErrorMessage }}
            </AppErrorText>
          </span>
        </div>
        <div class="flex flex-row items-end" aria-live="polite">
          <p class="heading-xl me-8" aria-hidden="true">
            {{ familyStore.summary_totalMontlyCost }}
          </p>
          <p class="heading-xs mb-4" aria-hidden="true">kr/mån</p>
          <p class="sr-only">
            {{ familyStore.summary_totalMontlyCost }} kronor i månaden för
            familjeabonnemangen
          </p>
        </div>
      </div>
      <div class="px-16 md:px-24 pb-16 md:pb-24 pt-8">
        <AppButton
          v-if="!familyStore.ui.isBuyButtonLoading"
          full-width
          @click="clickHandler"
        >
          Lägg till och fortsätt
        </AppButton>
        <LazyRoundedLoadingBox
          v-if="familyStore.ui.isBuyButtonLoading"
          message="Lägger till familjeabonnemang"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { EXISTING_CUSTOMER_ALLOWED_CONNECTIONS_ERROR_ID } from '~/models/FamilyDrawerElementIds.model';
import { family_addFamilyPlanDataToPdp } from '~/utils/family/addFamilyPlanDataToPdp';

const { $api } = useNuxtApp();
const familyStore = useFamilyStore();
const containerElement: Ref<HTMLElement | null> = ref(null);
const fixedElement: Ref<HTMLElement | null> = ref(null);
const { pushAddToCartEvent } = useGoogleAnalyticsEvents();

const goToCheckout = async () => {
  familyStore.ui.isBuyButtonLoading = true;
  try {
    await $api.ShoppingCartModule.AddBundleToCart(
      familyStore.requestPayloadForAddToCart,
    );
    pushAddToCartEvent(familyStore.trackingData);
    navigateTo('/shop/varukorg', { external: true });
  } catch (e) {
    familyStore.ui.generalErrorMessage =
      'Kunde inte lägga till abonnemangen till varukorgen just nu. Vänligen försök igen.';
    if (isNuxtError(e)) {
      logError(e);
    }
  } finally {
    familyStore.ui.isBuyButtonLoading = false;
  }
};

async function clickHandler() {
  familyStore.ui.generalErrorMessage = undefined;

  if (familyStore.existingCustomerSelectedPlanAllowedConnections === 0) {
    document
      .getElementById(EXISTING_CUSTOMER_ALLOWED_CONNECTIONS_ERROR_ID)
      ?.focus();
    return;
  }

  if (!familyStore.ui.isProductPage) {
    goToCheckout();
    return;
  }

  await family_addFamilyPlanDataToPdp();
}

const {
  existingCustomer,
  inputs,
  numberOfFamilyPlanConnections,
  selectedBindingPeriod,
  selectedFamilyPlan,
  ui,
} = storeToRefs(familyStore);

// Clear error message if something other than the error message is changed in family store
watch(
  [
    ui,
    existingCustomer,
    inputs,
    numberOfFamilyPlanConnections,
    selectedBindingPeriod,
    selectedFamilyPlan,
  ],
  ([ui], [prevUi]) => {
    if (ui.generalErrorMessage !== prevUi.generalErrorMessage) {
      return;
    }

    familyStore.ui.generalErrorMessage = undefined;
  },
);

onMounted(() => {
  familyStore.ui.isBuyButtonLoading = false;

  const setSizeOfFixedDiv = () => {
    if (containerElement.value && fixedElement.value) {
      fixedElement.value.style.width =
        containerElement.value.offsetWidth + 'px';
    }
  };

  setSizeOfFixedDiv();

  useResizeObserver(containerElement, () => {
    setSizeOfFixedDiv();
  });
});
</script>
